import { Box, Chip, Collapse, Icon, Stack, Typography } from '@mui/material'
import React, { useState } from 'react'

const BehavioralScore = ({ data }) => {
  const [expanded, setExpanded] = useState(false)

  const freedom = data?.personality_traits.freedom
  const adventurous = data?.personality_traits.adventurous
  const teamwork = data?.personality_traits.teamwork
  const systematic = data?.personality_traits.systematic
  const ambitious = data?.personality_traits.ambitious

  const [personalityLabel, setPersonalityLabel] = useState({
    dominant: 'Freedom',
    recessive: 'Structure',
  })
  const [personalityData, setPersonalityData] = useState(freedom)

  const chipStyles = {
    py: 2.5,
    px: 1,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'gray-line.main',
    borderRadius: '32px',
    '&:hover': {
      backgroundColor: 'primary.light',
      color: 'dark-text.main',
      borderColor: 'primary.main',
      borderWidth: 1,
      borderStyle: 'solid',
    },
  }

  return (
    <Box
      width="100%"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'start',
        gap: '24px',
      }}
    >
      <Typography textAlign="center" fontWeight={600} width="100%" variant="subtitle1">
        Behavioral Score
      </Typography>
      <Box
        width="100%"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1}>
          <Chip
            sx={{
              ...chipStyles,
              backgroundColor:
                personalityLabel.dominant === 'Freedom' ? 'primary.main' : 'white.main',
              color: personalityLabel.dominant === 'Freedom' ? 'text.primary' : 'dark-text.main',
            }}
            label="Freedom"
            onClick={() => {
              setPersonalityLabel({ dominant: 'Freedom', recessive: 'Structured' })
              setPersonalityData(freedom)
            }}
          />
          <Chip
            sx={{
              ...chipStyles,
              backgroundColor:
                personalityLabel.dominant === 'Adventurous' ? 'primary.main' : 'white.main',
              color:
                personalityLabel.dominant === 'Adventurous' ? 'text.primary' : 'dark-text.main',
            }}
            label="Adventurous"
            onClick={() => {
              setPersonalityLabel({ dominant: 'Adventurous', recessive: 'Laidback' })
              setPersonalityData(adventurous)
            }}
          />
          <Chip
            sx={{
              ...chipStyles,
              backgroundColor:
                personalityLabel.dominant === 'Teamwork' ? 'primary.main' : 'white.main',
              color: personalityLabel.dominant === 'Teamwork' ? 'text.primary' : 'dark-text.main',
            }}
            label="Teamwork"
            onClick={() => {
              setPersonalityLabel({ dominant: 'Teamwork', recessive: 'Independent' })
              setPersonalityData(teamwork)
            }}
          />
          <Chip
            sx={{
              ...chipStyles,
              backgroundColor:
                personalityLabel.dominant === 'Systematic' ? 'primary.main' : 'white.main',
              color: personalityLabel.dominant === 'Systematic' ? 'text.primary' : 'dark-text.main',
            }}
            label="Systematic"
            onClick={() => {
              setPersonalityLabel({ dominant: 'Systematic', recessive: 'Agile' })
              setPersonalityData(systematic)
            }}
          />
          <Chip
            sx={{
              ...chipStyles,
              backgroundColor:
                personalityLabel.dominant === 'Ambitious' ? 'primary.main' : 'white.main',
              color: personalityLabel.dominant === 'Ambitious' ? 'text.primary' : 'dark-text.main',
            }}
            label="Ambitious"
            onClick={() => {
              setPersonalityLabel({ dominant: 'Ambitious', recessive: 'Fulfilled' })
              setPersonalityData(ambitious)
            }}
          />
        </Stack>
      </Box>

      <Box>
        <Collapse collapsedSize={100} in={expanded}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              gap: '12px',
              borderLeft: 1,
              borderRight: 1,
              borderTop: 1,
              borderRadius: ' 4.5px 4.5px 0px 0px',
              borderColor: 'gray-line.main',
              paddingX: '20px',
              paddingTop: '20px',
            }}
          >
            <Typography variant="h4" fontWeight={600}>
              {`${personalityData?.dominant_percentage}% ${personalityLabel.dominant}`}
            </Typography>
            <Typography variant="body1">{personalityData?.description}</Typography>
          </Box>
        </Collapse>
        {!expanded && (
          <Box
            sx={{
              borderLeft: 1,
              borderRight: 1,
              borderBottom: 1,
              borderColor: 'gray-line.main',
              paddingBottom: '20px',
            }}
          >
            <Typography
              onClick={() => setExpanded(!expanded)}
              sx={{
                color: 'alternate.main',
                textDecoration: 'underline',
                '&:hover': {
                  cursor: 'pointer',
                },
                paddingX: '20px',
              }}
            >
              Show more
            </Typography>
          </Box>
        )}
        {expanded && (
          <Box
            sx={{
              borderLeft: 1,
              borderRight: 1,
              borderBottom: 1,
              borderColor: 'gray-line.main',
              paddingBottom: '20px',
            }}
          >
            <Typography
              onClick={() => setExpanded(!expanded)}
              sx={{
                color: 'alternate.main',
                textDecoration: 'underline',
                '&:hover': {
                  cursor: 'pointer',
                },
                paddingX: '20px',
              }}
            >
              Less details
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            borderLeft: 1,
            borderRight: 1,
            borderBottom: 1,
            borderRadius: ' 0px 0px 4.5px 4.5px',
            borderColor: 'gray-line.main',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 20px 16px 20px',
              width: '100%',
              gap: '12px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                position: 'relative',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color:
                    personalityData?.dominant_percentage < 50
                      ? 'primary.main'
                      : personalityData?.dominant_percentage === 50
                      ? 'primary.main'
                      : 'light-gray-text.main',
                }}
              >
                {`${100 - personalityData?.dominant_percentage}% ${personalityLabel.recessive}`}
              </Typography>
              <Typography sx={{ position: 'absolute', left: '48.5%' }}>vs</Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color:
                    personalityData?.dominant_percentage > 50
                      ? 'primary.main'
                      : personalityData?.dominant_percentage === 50
                      ? 'primary.main'
                      : 'light-gray-text.main',
                }}
              >
                {`${personalityData?.dominant_percentage}% ${personalityLabel.dominant}`}
              </Typography>
            </Box>
            <Box
              sx={{
                height: '10px',
                width: '100%',
                position: 'relative',
                borderRadius: '4.5px',
              }}
              backgroundColor={
                personalityData?.dominant_percentage > 50 ? 'primary.main' : `gray-line.main`
              }
            >
              <Box
                sx={{
                  backgroundColor: 'primary.main',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  top: '-50%',
                  right:
                    personalityData?.dominant_percentage > 50
                      ? `${personalityData?.dominant_percentage - 2}%`
                      : personalityData?.dominant_percentage === 50
                      ? '48.5%'
                      : `${personalityData?.dominant_percentage - 2}%`,
                  transition: 'right 0.5s ease, left 0.5s ease',
                }}
              >
                <Icon
                  sx={{
                    color: 'text.primary',
                    fontSize: '18px',
                  }}
                >
                  check
                </Icon>
              </Box>
              <Box
                sx={{
                  height: '10px',
                  borderRadius: '4.5px',
                }}
                backgroundColor={
                  personalityData?.dominant_percentage > 50 ? 'gray-line.main' : `primary.main`
                }
                width={
                  personalityData?.dominant_percentage === 50
                    ? `${100}%`
                    : `${100 - personalityData?.dominant_percentage}%`
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
export default BehavioralScore
